import { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { collection, getDocs, query, where, deleteDoc, doc } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";

export default function Dashboard() {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const navigate = useNavigate();

  useEffect(() => {
    loadResumes();
  }, []);

  const loadResumes = async () => {
    try {
      const q = query(
        collection(db, "resumes"),
        where("uid", "==", auth.currentUser.uid)
      );
      const querySnapshot = await getDocs(q);
      const resumesList = [];
      querySnapshot.forEach((doc) => {
        resumesList.push({ 
          id: doc.id, 
          ...doc.data(),
          // Handle createdAt properly
          createdAt: doc.data().createdAt ? doc.data().createdAt.toDate() : new Date()
        });
      });
      setResumes(resumesList);
    } catch (error) {
      console.error("Error loading resumes:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort resumes
  const filteredResumes = resumes
    .filter(resume => 
      resume.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resume.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      searchTerm === ""
    )
    .sort((a, b) => {
      if (sortBy === "newest") {
        return new Date(b.createdAt) - new Date(a.createdAt);
      } else {
        return new Date(a.createdAt) - new Date(b.createdAt);
      }
    });

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this resume?")) {
      try {
        await deleteDoc(doc(db, "resumes", id));
        setResumes(resumes.filter(resume => resume.id !== id));
        alert("Resume deleted!");
      } catch (error) {
        console.error("Error deleting resume:", error);
        alert("Failed to delete resume. Please try again.");
      }
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const handleEdit = (id) => {
    // Navigate to edit page with the resume ID
    navigate(`/edit/${id}`);
  };

  if (loading) return <div className="loading">Loading your resumes...</div>;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>My Resumes</h1>
        <div className="header-actions">
          <button onClick={() => navigate("/create")} className="btn-primary">
            + Create New Resume
          </button>
          <button onClick={handleLogout} className="btn-logout">
            Logout
          </button>
        </div>
      </div>

      {/* Search and Sort Tools */}
      <div className="dashboard-tools">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search resumes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select 
          className="sort-select"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
        </select>
      </div>

      {/* Stats Overview */}
      <div className="stats-overview">
        <div className="stat-card">
          <div className="stat-number">{resumes.length}</div>
          <div className="stat-label">Total Resumes</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {resumes.filter(r => r.name && r.name.trim() !== "").length}
          </div>
          <div className="stat-label">Completed</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {new Date().toLocaleDateString('en-US', { month: 'short' })}
          </div>
          <div className="stat-label">This Month</div>
        </div>
      </div>

      {filteredResumes.length === 0 ? (
        <div className="empty-state">
          <h2>
            {searchTerm ? "No matching resumes found" : "No resumes yet"}
          </h2>
          <p>
            {searchTerm 
              ? "Try a different search term or create a new resume"
              : "Create your first resume to get started!"}
          </p>
          <button onClick={() => navigate("/create")} className="btn-primary">
            Create New Resume
          </button>
        </div>
      ) : (
        <>
          <div className="resumes-info">
            <p>Showing {filteredResumes.length} of {resumes.length} resumes</p>
          </div>
          <div className="resumes-grid">
            {filteredResumes.map((resume) => (
              <div key={resume.id} className="resume-card">
                <div className="card-content">
                  <h3>{resume.name  || "Untitled Resume"}</h3>
                  <p className="card-title">{resume.title || "No title"}</p>
                  <div className="card-meta">
                    <p className="card-date">
                      Created: {new Date(resume.createdAt).toLocaleDateString()}
                    </p>
                    {resume.updatedAt && (
                      <p className="card-date">
                         Updated: {new Date(resume.updatedAt.toDate()).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
                <div className="card-actions">
                  <button 
                    onClick={() => navigate(`/preview/${resume.id}`)} 
                    className="btn-view"
                    title="Preview Resume"
                  >
                     View
                  </button>
                  <button 
                    onClick={() => handleEdit(resume.id)} 
                    className="btn-edit"
                    title="Edit Resume"
                  >
                     Edit
                  </button>
                  <button 
                    onClick={() => handleDelete(resume.id)} 
                    className="btn-delete"
                    title="Delete Resume"
                  >
                     Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}